import { useCallback, useEffect, useState } from "react";
import { hasUsableShellTranslations, initI18n } from "./i18n.js";
import { TranslationLoadError, TRANSLATION_TIMEOUT_MS, type TranslationFailure } from "./i18nLifecycle.js";
import type { ResolvedLocale } from "./locale.js";

type StartupState = {
  locale: ResolvedLocale;
  attempt: number;
  status: "loading" | "ready" | "error";
  error: TranslationFailure | null;
};

/** Only the current locale/attempt may reveal the shell, including during StrictMode remounts. */
export function useI18nStartup(locale: ResolvedLocale, enabled: boolean) {
  const [hasBeenReady, setHasBeenReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<StartupState>({
    locale, attempt, status: "loading", error: null
  });
  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setState({ locale, attempt, status: "loading", error: null });
    // Also bound time waiting behind an earlier locale request. Bundle loading
    // has its own deadline; this timer never starts another network request.
    const timer = setTimeout(() => {
      if (!active) return;
      active = false;
      setState({ locale, attempt, status: "error", error: "translations_timeout" });
    }, TRANSLATION_TIMEOUT_MS + 1_000);
    void initI18n(locale).then(() => {
      if (!active) return;
      if (!hasUsableShellTranslations(locale)) throw new TranslationLoadError("translations_unavailable");
      setHasBeenReady(true);
      document.documentElement.lang = locale;
      setState({ locale, attempt, status: "ready", error: null });
    }).catch((error: unknown) => {
      if (!active) return;
      setState({
        locale, attempt, status: "error",
        error: error instanceof TranslationLoadError ? error.code : "translations_unavailable"
      });
    }).finally(() => clearTimeout(timer));
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [attempt, enabled, locale]);

  // A locale change gates this render, before the next effect can reset state.
  const current = state.locale === locale && state.attempt === attempt;
  const status = !enabled ? "idle" : current ? state.status : "loading";
  return { status, error: current ? state.error : null, retry, hasBeenReady } as const;
}

